'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Fab from '@mui/material/Fab';
import Zoom from '@mui/material/Zoom';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { trackCTAClick } from '../../../lib/analytics';

/**
 * Sticky CTA Button
 *
 * A floating action button that appears when the user scrolls down.
 * Used primarily in Variant B (Mobile-First Vertical) on mobile devices.
 *
 * Features:
 * - Appears after scrolling past a threshold
 * - Smooth scroll to content input area when clicked
 * - Respects user's reduced-motion preference
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface StickyCTAProps {
  /** Scroll threshold (in pixels) before showing the button */
  threshold?: number;
  /** Target element ID to scroll to */
  targetId?: string;
  /** Custom label (overrides i18n) */
  label?: string;
  /** Icon to display */
  icon?: 'add' | 'arrow';
  /** Position from bottom */
  bottom?: number;
  /** Position from right */
  right?: number;
  /** Callback when clicked */
  onClick?: () => void;
}

export function StickyCTA({
  threshold = 300,
  targetId = 'content-input',
  label,
  icon = 'add',
  bottom = 24,
  right = 24,
  onClick,
}: StickyCTAProps) {
  const t = useTranslations('creator.landing.stickyCTA');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > threshold);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  const handleClick = () => {
    // Track CTA click for analytics (AC6)
    trackCTAClick('sticky_cta', 'floating_button');

    if (onClick) {
      onClick();
      return;
    }

    // Default behavior: scroll to target element
    const target = document.getElementById(targetId);
    if (target) {
      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      target.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });

      // Focus the first input in the target area for accessibility
      setTimeout(() => {
        const input = target.querySelector('input, textarea');
        if (input instanceof HTMLElement) {
          input.focus();
        }
      }, prefersReducedMotion ? 0 : 500);
    }
  };

  const displayLabel = label || t('label');
  const IconComponent = icon === 'arrow' ? KeyboardArrowDownIcon : AddIcon;

  return (
    <Zoom in={isVisible}>
      <Fab
        color="primary"
        variant="extended"
        onClick={handleClick}
        aria-label={displayLabel}
        sx={{
          position: 'fixed',
          bottom,
          right,
          zIndex: 1000,
          // Ensure minimum touch target size
          minHeight: 48,
          minWidth: 48,
          px: 3,
          // Add shadow for visibility
          boxShadow: 4,
          '&:hover': {
            boxShadow: 6,
          },
          // Respect RTL
          '[dir="rtl"] &': {
            right: 'auto',
            left: right,
          },
        }}
      >
        <IconComponent sx={{ mr: 1 }} />
        {displayLabel}
      </Fab>
    </Zoom>
  );
}
